import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useProtectedNavigate, POST_LOGIN_REDIRECT_KEY } from '@/App';
import axios from 'axios';
import { AUTH } from '@/constants/testIds';
import { TESTIMONIALS } from '@/constants/testIds';
import { Building2, CheckCircle, Users, Shield, FileCheck, TrendingUp, Compass, Star, Quote, AlertTriangle, HardHat, Hammer, Wrench, Upload, MessageSquare, Trophy, ArrowRight, Lock, DollarSign } from 'lucide-react';
import TwoFAChallenge from '@/components/TwoFAChallenge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const { login } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [adminMode, setAdminMode] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'client',
    phone: '',
    organization: '',
    referred_by_code: '',
    bio: '',
    expertise: '',
    hourly_rate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testimonials, setTestimonials] = useState([]);
  // 2FA challenge state: when /auth/login returns requires_2fa, we capture the
  // short-lived challenge_token and render <TwoFAChallenge/> instead of the
  // credential form. On success we call login(user, token) and bounce to any
  // `postLoginRedirect` saved in sessionStorage (set by useProtectedNavigate).
  const [twoFa, setTwoFa] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Auto-open the auth panel when a protected link routes the user here with
  // ?auth=1 (see useProtectedNavigate in App.js).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('auth') === '1') {
      setShowAuth(true);
      setIsLogin(true);
      setAdminMode(false);
    }
  }, [location.search]);

  // Helper used after every successful sign-in (password-only OR password+2FA)
  // to consume the saved redirect path. If none is set, react-router falls back
  // to the role-based <Navigate> in App.js.
  const consumePostLoginRedirect = () => {
    let target = null;
    try { target = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY); } catch (_e) { /* ignore */ }
    if (target) {
      try { sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY); } catch (_e) { /* ignore */ }
      navigate(target);
    }
  };

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/reviews/public`)
      .then((res) => { if (!cancelled) setTestimonials(res.data || []); })
      .catch(() => { /* graceful: section hides if fetch fails */ });
    return () => { cancelled = true; };
  }, []);

  const extractErrorMessage = (err) => {
    // Robust error extraction so we never swallow backend messages or render
    // an array/object as React children. Handles:
    //   • string `detail` (e.g. "Email already registered")
    //   • array `detail` (Pydantic 422 — now flattened server-side, kept for safety)
    //   • Pydantic loc/msg dicts
    //   • network errors (no response at all)
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d)))
        .join('; ');
    }
    if (detail && typeof detail === 'object') {
      return detail.msg || JSON.stringify(detail);
    }
    if (err?.message && /network/i.test(err.message)) {
      return 'Could not reach the server. Please check your internet connection and try again.';
    }
    return err?.message || 'Something went wrong. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = { ...formData };
      if (!isLogin && payload.role === 'consultant') {
        payload.expertise = payload.expertise
          ? payload.expertise.split(',').map((s) => s.trim()).filter(Boolean)
          : [];
        payload.hourly_rate = payload.hourly_rate ? parseFloat(payload.hourly_rate) : null;
      } else if (!isLogin) {
        delete payload.bio;
        delete payload.expertise;
        delete payload.hourly_rate;
      }
      // Client-side guard for min password length so we never even hit the API with a bad pwd
      if (!isLogin && payload.password && payload.password.length < 6) {
        setError('Password must be at least 6 characters.');
        setLoading(false);
        return;
      }
      const response = await axios.post(`${API}${endpoint}`, payload);
      // 2FA branch: login may return a challenge instead of {user, token}
      if (isLogin && response.data?.requires_2fa) {
        setTwoFa({
          challengeToken: response.data.challenge_token,
          email: response.data.email || payload.email,
          deliveryTo: response.data.delivery?.masked_to,
          devOtpCode: response.data.dev_otp_code,
          expiresInMinutes: response.data.delivery?.expires_in_minutes,
        });
        setLoading(false);
        return;
      }
      const { user, token } = response.data;
      // Admin Sign In flow: reject non-admin accounts trying to use the admin entry
      if (adminMode && user.role !== 'admin') {
        setError(
          'This sign-in is reserved for the All Build Consult administration team. ' +
          'Use the regular Sign In button if you are a client or consultant.'
        );
        setLoading(false);
        return;
      }
      login(user, token);
      consumePostLoginRedirect();
      // App.js routes admins to /admin and everyone else to /dashboard automatically.
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Render the 2FA challenge whenever an in-flight login asks for it.
  if (twoFa) {
    return (
      <div className={`min-h-screen ${adminMode ? 'bg-slate-900' : 'bg-slate-50'} flex items-center justify-center p-6`}>
        <TwoFAChallenge
          challengeToken={twoFa.challengeToken}
          email={twoFa.email}
          deliveryTo={twoFa.deliveryTo}
          devOtpCode={twoFa.devOtpCode}
          expiresInMinutes={twoFa.expiresInMinutes}
          onComplete={({ user, token }) => {
            setTwoFa(null);
            if (adminMode && user?.role !== 'admin') {
              setError('This sign-in is reserved for the All Build Consult administration team.');
              return;
            }
            login(user, token);
            consumePostLoginRedirect();
          }}
          onCancel={() => { setTwoFa(null); setError(''); }}
        />
      </div>
    );
  }

  if (showAuth) {
    return (
      <div className={`min-h-screen ${adminMode ? 'bg-slate-900' : 'bg-white'} flex items-center justify-center p-6`}>
        <div className="w-full max-w-md">
          {/* Distinct admin banner */}
          {adminMode && (
            <div
              data-testid="admin-signin-banner"
              className="mb-4 grid-border bg-slate-800 border-slate-700 p-4 flex items-center gap-3"
            >
              <div className="w-10 h-10 bg-red-600 rounded-sm flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-red-400 mb-0.5">
                  Restricted area
                </p>
                <p className="text-sm text-white font-medium">
                  All Build Consult — Administration Sign In
                </p>
              </div>
            </div>
          )}
          <div className="grid-border bg-white p-8">
            <div className="mb-6">
              <h2 className="text-3xl font-bold tracking-tight text-black" style={{fontFamily: 'Outfit'}}>
                {adminMode ? 'Administrator Sign In' : (isLogin ? 'Sign In' : 'Create Account')}
              </h2>
              <p className="text-sm text-slate-600 mt-2">
                {adminMode
                  ? 'For the All Build Consult management team only.'
                  : (isLogin ? 'Access your construction dashboard' : 'Join our consulting platform')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && !adminMode && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">Full Name</label>
                    <input
                      data-testid={AUTH.nameInput}
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-2">Role</label>
                    <select
                      data-testid={AUTH.roleSelect}
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    >
                      <option value="client">Client</option>
                      <option value="consultant">Consultant</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-2">Phone (Optional)</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-2">Organization (Optional)</label>
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Referral Code (Optional)
                    </label>
                    <input
                      data-testid="register-referral-code-input"
                      type="text"
                      name="referred_by_code"
                      value={formData.referred_by_code}
                      onChange={handleChange}
                      placeholder="e.g. 9D26F3F8"
                      maxLength={12}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent uppercase tracking-wider"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Have a code from an existing customer? Enter it here.
                    </p>
                  </div>

                  {formData.role === 'consultant' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-black mb-2">Bio</label>
                        <textarea
                          name="bio"
                          value={formData.bio}
                          onChange={handleChange}
                          rows="3"
                          placeholder="Brief introduction about yourself and experience..."
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-black mb-2">Expertise (comma-separated)</label>
                        <input
                          type="text"
                          name="expertise"
                          value={formData.expertise}
                          onChange={handleChange}
                          placeholder="e.g. Structural, Electrical, Compliance"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-black mb-2">Hourly Rate (AUD)</label>
                        <input
                          type="number"
                          step="1"
                          name="hourly_rate"
                          value={formData.hourly_rate}
                          onChange={handleChange}
                          placeholder="150"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-black mb-2">Email</label>
                <input
                  data-testid={AUTH.emailInput}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Password</label>
                <input
                  data-testid={AUTH.passwordInput}
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  minLength={6}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                />
                {!isLogin && (
                  <p className="mt-1 text-xs text-slate-500">
                    Use at least <strong>6 characters</strong>. Mix letters &amp; numbers for stronger security.
                  </p>
                )}
              </div>

              {error && (
                <div
                  data-testid="auth-error-banner"
                  className="p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800"
                >
                  <p>{error}</p>
                  {/* If email is taken, offer Sign In as a one-click recovery */}
                  {!isLogin && /already registered/i.test(error) && (
                    <button
                      type="button"
                      data-testid="auth-error-switch-to-signin"
                      onClick={() => { setIsLogin(true); setError(''); }}
                      className="mt-2 text-xs underline font-semibold text-red-900 hover:text-red-700"
                    >
                      Sign in to this account instead →
                    </button>
                  )}
                  {/* If admin reset is needed, hint */}
                  {isLogin && /invalid email or password/i.test(error) && (
                    <p className="mt-2 text-xs text-red-700">
                      Forgot your password? Contact{' '}
                      <a href="mailto:admin@allbuildconsult.net" className="underline font-semibold">
                        admin@allbuildconsult.net
                      </a>{' '}
                      to request a reset.
                    </p>
                  )}
                </div>
              )}

              <button
                data-testid={AUTH.submitButton}
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all font-medium"
              >
                {loading ? 'Processing...' : (adminMode ? 'Sign In as Administrator' : (isLogin ? 'Sign In' : 'Create Account'))}
              </button>

              {!adminMode && (
                <button
                  data-testid={AUTH.switchMode}
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                  }}
                  className="w-full text-sm text-blue-600 hover:text-blue-800"
                >
                  {isLogin ? 'Need an account? Register' : 'Already have an account? Sign In'}
                </button>
              )}
              {adminMode && (
                <button
                  data-testid="admin-signin-exit"
                  type="button"
                  onClick={() => { setAdminMode(false); setShowAuth(false); setError(''); }}
                  className="w-full text-xs text-slate-500 hover:text-black"
                >
                  ← Back to client / consultant sign in
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <nav className="bg-white border-b border-slate-200 fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Compass className="w-8 h-8 text-black" strokeWidth={2.25} />
              <span className="text-xl font-bold tracking-tight" style={{fontFamily: 'Outfit'}}>All Build Consult</span>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => window.location.href = '/about'}
                className="px-4 py-2 text-sm text-black hover:text-blue-600 transition-colors"
              >
                About
              </button>
              <button
                onClick={() => window.location.href = '/services'}
                className="px-4 py-2 text-sm text-black hover:text-blue-600 transition-colors"
              >
                Services
              </button>
              <button
                data-testid={AUTH.loginButton}
                onClick={() => {
                  setAdminMode(false);
                  setShowAuth(true);
                  setIsLogin(true);
                }}
                className="px-6 py-2 border border-slate-300 rounded-sm hover:bg-slate-50 transition-all text-sm font-medium"
              >
                Sign In
              </button>
              <button
                data-testid={AUTH.registerButton}
                onClick={() => {
                  setAdminMode(false);
                  setShowAuth(true);
                  setIsLogin(false);
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-all text-sm font-medium"
              >
                Get Started
              </button>
              <button
                data-testid="admin-signin-trigger"
                onClick={() => {
                  setAdminMode(true);
                  setIsLogin(true);
                  setShowAuth(true);
                }}
                title="Restricted — All Build Consult administration team only"
                className="px-3 py-2 text-xs text-slate-500 hover:text-black transition-colors inline-flex items-center gap-1 border border-transparent hover:border-slate-300 rounded-sm"
              >
                <Lock className="w-3 h-3" />
                Admin
              </button>
            </div>
          </div>
        </div>
      </nav>

      <section className="hero-section min-h-screen flex items-center pt-20">
        <div className="hero-overlay w-full py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl">
              <h1 className="text-6xl font-bold tracking-tight text-black mb-6" style={{fontFamily: 'Outfit'}}>
                Expert Construction Consulting for Every Build
              </h1>
              <p className="text-xl text-slate-700 mb-8 leading-relaxed">
                Professional guidance & reports for property managers, DIY projects, homeowners, trades and building projects. From defects reporting, rectification, recommendation, repairs, third party assessments, with a licensed builder or project consultant, we have got you covered.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowAuth(true);
                    setIsLogin(false);
                  }}
                  className="px-8 py-4 bg-black text-white rounded-sm hover:bg-slate-800 transition-all font-medium text-lg"
                >
                  Start Your Project
                </button>
                <button
                  onClick={() => window.location.href = '/services'}
                  className="px-8 py-4 border border-slate-300 bg-white rounded-sm hover:bg-slate-50 transition-all font-medium text-lg"
                >
                  View Services
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === ABC Marketing — As easy as ABC === */}
      <section
        data-testid="landing-abc-section"
        className="py-24 px-6 bg-white border-t border-slate-100 relative overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-blue-50 blur-3xl opacity-70"
        />
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] text-blue-700 mb-3 font-semibold">
              How it works
            </p>
            <h2
              className="text-5xl md:text-6xl font-bold tracking-tighter text-black mb-4"
              style={{ fontFamily: 'Outfit' }}
            >
              All Build Consult is as easy as
              <br />
              <span className="text-blue-600">A · B · C</span>
            </h2>
            <p className="text-lg text-slate-600">
              Three clear steps to bring building communication, clarification and{' '}
              <strong className="text-black">closure</strong> to your build matter.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* A */}
            <article
              data-testid="abc-step-a"
              className="grid-border bg-slate-50 p-8 relative hover-lift overflow-hidden"
            >
              <div
                aria-hidden
                className="absolute -top-6 -right-2 text-[160px] font-black leading-none text-blue-100 select-none"
                style={{ fontFamily: 'Outfit' }}
              >
                A
              </div>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-600 text-white text-xs font-bold uppercase tracking-wider rounded-sm mb-4">
                  Step A
                  <Upload className="w-3.5 h-3.5" />
                </div>
                <h3
                  className="text-2xl font-bold text-black mb-2"
                  style={{ fontFamily: 'Outfit' }}
                >
                  <span className="text-blue-600">A</span>ll documents,
                  pictures &amp; photos
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Upload everything that frames your build matter — defect photos, scope
                  documents, plans, quotes, contracts. The more we see up-front, the faster
                  the All Build Consult team can act.
                </p>
              </div>
            </article>

            {/* B */}
            <article
              data-testid="abc-step-b"
              className="grid-border bg-slate-50 p-8 relative hover-lift overflow-hidden"
            >
              <div
                aria-hidden
                className="absolute -top-6 -right-2 text-[160px] font-black leading-none text-blue-100 select-none"
                style={{ fontFamily: 'Outfit' }}
              >
                B
              </div>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-600 text-white text-xs font-bold uppercase tracking-wider rounded-sm mb-4">
                  Step B
                  <MessageSquare className="w-3.5 h-3.5" />
                </div>
                <h3
                  className="text-2xl font-bold text-black mb-2"
                  style={{ fontFamily: 'Outfit' }}
                >
                  <span className="text-blue-600">B</span>uilding concerns &amp; help
                  seeking
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Tell us your concerns, observations and the outcome you're chasing —
                  plain English is welcome. Comments, questions and help-seeking notes
                  shape the scope of the review and the report you'll receive.
                </p>
              </div>
            </article>

            {/* C */}
            <article
              data-testid="abc-step-c"
              className="grid-border bg-slate-50 p-8 relative hover-lift overflow-hidden"
            >
              <div
                aria-hidden
                className="absolute -top-6 -right-2 text-[160px] font-black leading-none text-blue-100 select-none"
                style={{ fontFamily: 'Outfit' }}
              >
                C
              </div>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-600 text-white text-xs font-bold uppercase tracking-wider rounded-sm mb-4">
                  Step C
                  <Trophy className="w-3.5 h-3.5" />
                </div>
                <h3
                  className="text-2xl font-bold text-black mb-2"
                  style={{ fontFamily: 'Outfit' }}
                >
                  <span className="text-blue-600">C</span>onclusion &amp; result
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Receive a structured report with conclusions, recommended next steps
                  and a clear path to close the matter — building communication,
                  clarification, and confidence in the outcome.
                </p>
              </div>
            </article>
          </div>

          {/* Closing tagline */}
          <div className="mt-12 text-center max-w-2xl mx-auto">
            <p className="text-base text-slate-700">
              <span className="font-semibold text-black">A</span>ll documents.{' '}
              <span className="font-semibold text-black">B</span>uilding concerns.{' '}
              <span className="font-semibold text-black">C</span>onclusion &amp; closure —{' '}
              <em className="text-blue-700">always in that order, every time.</em>
            </p>
            <button
              data-testid="abc-cta"
              onClick={() => {
                setShowAuth(true);
                setIsLogin(false);
              }}
              className="mt-5 px-6 py-3 bg-black text-white text-sm font-semibold rounded-sm hover:bg-slate-800 transition-all inline-flex items-center gap-2"
            >
              Start your ABC build review
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight text-black mb-4" style={{fontFamily: 'Outfit'}}>
              Schedule of Reporting Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Six service categories covering every stage of your build — from defect investigations to specialist referrals.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="grid-border bg-white p-8 hover-lift">
              <div className="w-12 h-12 bg-black rounded-sm flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{fontFamily: 'Outfit'}}>
                Building Defects
              </h3>
              <p className="text-slate-600">
                Defect reports, structural & specialist assessments, online scope reviews and supervised repairs — backed by photo documentation and clear next-step guidance.
              </p>
            </div>

            <div className="grid-border bg-white p-8 hover-lift">
              <div className="w-12 h-12 bg-black rounded-sm flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{fontFamily: 'Outfit'}}>
                Specialist Services
              </h3>
              <p className="text-slate-600">
                Roofing, plumbing leak tests, electrical &amp; solar, asbestos sampling, and full building reports — issued by licensed specialists with plain-English explanations.
              </p>
            </div>

            <div className="grid-border bg-white p-8 hover-lift">
              <div className="w-12 h-12 bg-black rounded-sm flex items-center justify-center mb-4">
                <HardHat className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{fontFamily: 'Outfit'}}>
                Construction Sequencing
              </h3>
              <p className="text-slate-600">
                Phase-by-phase ordering reviews and multi-trade coordination plans to keep your site moving on schedule, with milestone alignment baked in.
              </p>
            </div>

            <div className="grid-border bg-white p-8 hover-lift">
              <div className="w-12 h-12 bg-blue-600 rounded-sm flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{fontFamily: 'Outfit'}}>
                Cost Management
              </h3>
              <p className="text-slate-600">
                Itemised budget estimates and full cost breakdowns with market rates, risk analysis and savings opportunities — before you sign off on works.
              </p>
            </div>

            <div className="grid-border bg-white p-8 hover-lift">
              <div className="w-12 h-12 bg-blue-600 rounded-sm flex items-center justify-center mb-4">
                <Hammer className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{fontFamily: 'Outfit'}}>
                DIY Projects
              </h3>
              <p className="text-slate-600">
                Owner-builder &amp; DIY guidance, safety/risk assessments, and code compliance checks so home renovations stay safe, compliant and well-scoped.
              </p>
            </div>

            <div className="grid-border bg-white p-8 hover-lift relative border-red-300 ring-1 ring-red-200">
              <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white text-[10px] uppercase font-bold tracking-wider rounded-sm">
                Coming Soon
              </span>
              <div className="w-12 h-12 bg-red-600 rounded-sm flex items-center justify-center mb-4">
                <Wrench className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 pr-24" style={{fontFamily: 'Outfit'}}>
                Trade Works &amp; Administration
              </h3>
              <p className="text-slate-600 opacity-80">
                Quoting &amp; scoping reviews, contracts &amp; conditions, tenders &amp; costing, programs &amp; timelines, safety &amp; risk plans, and construction documentation — launching soon.
              </p>
            </div>
          </div>

          {/* Quick-jump links into the user area. Logged-out clicks bounce
              through the sign-in form and land on the requested page right
              after authentication (see useProtectedNavigate in App.js). */}
          <QuickAccessLinks />
        </div>
      </section>

      {testimonials.length > 0 && (
        <section
          data-testid={TESTIMONIALS.section}
          className="py-20 px-6 bg-white border-t border-slate-100"
        >
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mb-12">
              <p className="text-xs uppercase tracking-[0.2em] text-blue-700 mb-3 font-semibold">
                Client Stories
              </p>
              <h2 className="text-4xl font-bold tracking-tight text-black mb-3" style={{ fontFamily: 'Outfit' }}>
                Trusted by homeowners and builders across Australia
              </h2>
              <p className="text-base text-slate-600">
                Verified feedback from clients who completed an All Build Consult workflow.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.slice(0, 6).map((t) => (
                <article
                  key={t.id}
                  data-testid={TESTIMONIALS.card}
                  className="grid-border bg-slate-50 p-6 flex flex-col"
                >
                  <Quote className="w-6 h-6 text-blue-600 mb-3" />
                  <div className="flex items-center gap-0.5 mb-3 text-amber-500">
                    {Array.from({ length: t.stars }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                  <h3 className="text-base font-semibold text-black mb-2" style={{ fontFamily: 'Outfit' }}>
                    {t.title}
                  </h3>
                  <p className="text-sm text-slate-700 flex-1 whitespace-pre-wrap line-clamp-6">
                    {t.comment}
                  </p>
                  <div className="mt-4 pt-3 border-t border-slate-200 text-xs text-slate-500">
                    <span className="font-medium text-black">{t.client_name}</span>
                    {t.moderated_at && (
                      <span> · {new Date(t.moderated_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 px-6 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold tracking-tight mb-6" style={{fontFamily: 'Outfit'}}>
            Ready to Transform Your Construction Projects?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Join hundreds of homeowners and builders who trust All Build Consult for expert guidance
          </p>
          <button
            onClick={() => {
              setShowAuth(true);
              setIsLogin(false);
            }}
            className="px-8 py-4 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-all font-medium text-lg"
          >
            Get Started Free
          </button>
        </div>
      </section>

      <footer className="bg-white border-t border-slate-200 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-600">
          <p>&copy; 2026 All Build Consult. Professional construction consulting platform.</p>
        </div>
      </footer>
    </div>
  );
}

/**
 * Two-card "quick access" panel rendered at the bottom of the Schedule of
 * Reporting Services section on the landing page. Both cards route into the
 * authenticated user area; logged-out visitors are bounced to the sign-in
 * form with a return-to-target via `useProtectedNavigate` (see App.js).
 */
function QuickAccessLinks() {
  const goto = useProtectedNavigate();
  return (
    <div
      data-testid="landing-quick-links"
      className="mt-16 grid md:grid-cols-2 gap-5"
    >
      <button
        type="button"
        onClick={() => goto('/services')}
        data-testid="link-schedule-of-cost"
        className="group text-left grid-border bg-white p-7 hover-lift transition-all hover:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
      >
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-sm bg-emerald-600 text-white">
            <DollarSign className="w-5 h-5" />
          </span>
          <h3 className="text-lg font-semibold text-black" style={{ fontFamily: 'Outfit' }}>
            See Schedule of Cost
          </h3>
        </div>
        <p className="text-sm text-slate-600 mb-3">
          Full price list across every service category — deposits, full fees and
          inclusions, in AUD. Updated live from the All Build Consult catalog.
        </p>
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 group-hover:text-emerald-900">
          Open Schedule of Cost
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </button>

      <button
        type="button"
        onClick={() => goto('/consultants')}
        data-testid="link-need-expert-guidance"
        className="group text-left grid-border bg-blue-600 text-white p-7 hover-lift transition-all hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2"
      >
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-sm bg-white/15">
            <Users className="w-5 h-5" />
          </span>
          <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
            Need Expert Guidance?
          </h3>
        </div>
        <p className="text-sm text-blue-100 mb-3">
          Match with a licensed All Build Consult expert — defects, sequencing,
          compliance, cost management and more. Available once your pre-assessment
          and deposit are in.
        </p>
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-white group-hover:text-amber-200">
          Browse Expert Consultants
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </button>
    </div>
  );
}
